import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "react-query";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import {
  isEstateSuperAdmin,
  isPlatformSuperAdmin,
} from "./utils/permissions";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Estates from "./pages/Estates";
import Users from "./pages/Users";
import ResidentRegistrations from "./pages/ResidentRegistrations";
import Security from "./pages/Security";
import Notification from "./pages/Notification";
import AdminManagement from "./pages/AdminManagement";
import PropertyUnits from "./pages/PropertyUnits";
import AccessLogs from "./pages/AccessLogs";
import SettingsPage from "./pages/Settings";
import Layout from "./components/Layout";
import "./App.css";

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Component to handle default route based on user role
const DefaultRoute = () => {
  const { loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return <Dashboard />;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// Public Route Component (redirects if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  // Redirect authenticated users to their default page
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const AdminManagementRoute = () => {
  const { admin, user, loading } = useAuth();
  const currentUser = admin || user;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (
    isPlatformSuperAdmin(currentUser) ||
    isEstateSuperAdmin(currentUser)
  ) {
    return <AdminManagement />;
  }

  return <Navigate to="/" replace />;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <div className="App">
            <Routes>
              {/* Public Routes */}
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <Login />
                  </PublicRoute>
                }
              />

              {/* Protected Routes */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<DefaultRoute />} />
                <Route path="estates" element={<Estates />} />
                <Route path="users" element={<Users />} />
                <Route path="residents" element={<Users />} />
                <Route
                  path="resident-registrations"
                  element={<ResidentRegistrations />}
                />
                <Route path="property-units" element={<PropertyUnits />} />
                <Route path="units" element={<PropertyUnits />} />
                <Route path="security" element={<Security />} />
                <Route path="access-logs" element={<AccessLogs />} />
                <Route path="notification" element={<Notification />} />
                <Route path="broadcasts" element={<Notification />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="admins" element={<AdminManagementRoute />} />
              </Route>

              {/* Catch all - redirect to home (which will handle routing based on role) */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
